/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        MobileProfilPage.tsx
 * @description Page profil agent mobile - Infos utilisateur et deconnexion
 *
 * @version     2.4
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { OfflineIndicator, MobileNavBar, MobileMeteoWidget } from '@/components/mobile';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { getDatabaseStats, clearAllData } from '@/services/offlineDb';
import { useState, useEffect } from 'react';
import {
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  TrashIcon,
  CloudIcon,
  DevicePhoneMobileIcon,
  WifiIcon,
} from '@heroicons/react/24/outline';
import { clsx } from 'clsx';

export function MobileProfilPage() {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const { isOnline, pendingCount, sync, isSyncing } = useOfflineSync();

  const [dbStats, setDbStats] = useState<{
    interventions: number;
    zones: number;
    pendingActions: number;
  } | null>(null);

  // Charger les stats de la base locale
  useEffect(() => {
    getDatabaseStats().then(setDbStats).catch(console.error);
  }, [pendingCount]);

  const handleLogout = () => {
    if (pendingCount > 0) {
      if (!confirm(`${pendingCount} action(s) non synchronisee(s). Deconnexion quand meme ?`)) {
        return;
      }
    }
    logout();
    navigate('/login');
  };

  const handleClearCache = async () => {
    if (confirm('Effacer toutes les donnees locales ? Les actions non synchronisees seront perdues.')) {
      await clearAllData();
      setDbStats({ interventions: 0, zones: 0, pendingActions: 0 });
    }
  };

  const handleSync = async () => {
    if (isOnline) {
      await sync();
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-20">
      {/* Offline Indicator */}
      <OfflineIndicator />

      {/* Header */}
      <header className="bg-primary-600 text-white px-4 py-6">
        {/* Widget meteo */}
        <div className="flex justify-center mb-4">
          <MobileMeteoWidget icao="LFPO" />
        </div>
        {/* Infos profil */}
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 rounded-full bg-primary-500 flex items-center justify-center mb-2">
            <UserCircleIcon className="w-12 h-12" />
          </div>
          <h1 className="text-lg font-bold">{user?.nomComplet || user?.nom || 'Agent'}</h1>
          <p className="text-primary-200 text-sm">{user?.email}</p>
          <span className="mt-2 px-3 py-1 bg-primary-500 rounded-full text-xs font-medium">
            Agent Deneigement
          </span>
        </div>
      </header>

      {/* Contenu */}
      <main className="p-4 space-y-4">
        {/* Statut connexion */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Statut
          </h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-600">
                <WifiIcon className="w-5 h-5" />
                <span>Connexion</span>
              </div>
              <span className={clsx(
                'px-2 py-1 rounded-full text-xs font-medium',
                isOnline ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              )}>
                {isOnline ? 'En ligne' : 'Hors ligne'}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-600">
                <CloudIcon className="w-5 h-5" />
                <span>Actions en attente</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={clsx(
                  'px-2 py-1 rounded-full text-xs font-medium',
                  pendingCount > 0 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
                )}>
                  {pendingCount}
                </span>
                {pendingCount > 0 && isOnline && (
                  <button
                    onClick={handleSync}
                    disabled={isSyncing}
                    className="text-primary-600 text-sm font-medium"
                  >
                    {isSyncing ? 'Sync...' : 'Sync'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Donnees locales */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Donnees locales
          </h2>
          {dbStats && (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Interventions en cache</span>
                <span className="font-medium">{dbStats.interventions}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Zones en cache</span>
                <span className="font-medium">{dbStats.zones}</span>
              </div>
            </div>
          )}
          <button
            onClick={handleClearCache}
            className="mt-3 w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-gray-300 text-gray-600 text-sm"
          >
            <TrashIcon className="w-4 h-4" />
            Vider le cache local
          </button>
        </div>

        {/* Infos application */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Application
          </h2>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Version</span>
              <span className="font-medium">2.4 (PWA)</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Module</span>
              <span className="font-medium">Deneigement</span>
            </div>
          </div>
        </div>

        {/* Deconnexion */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-red-50 border border-red-200 text-red-600 font-medium"
        >
          <ArrowRightOnRectangleIcon className="w-5 h-5" />
          Se deconnecter
        </button>
      </main>

      {/* Navbar */}
      <MobileNavBar />
    </div>
  );
}

export default MobileProfilPage;
